import { useCallback, useEffect, useMemo, useState } from 'react';
import type { NavModule } from '@biovet/ui';
import { MODULES } from '../nav.js';

/**
 * Addresses for the screens.
 *
 * **Every screen has a URL now, and that is the whole point.** The nav used to
 * be a pair of `useState` keys, which meant a screen could only be reached by
 * clicking: no bookmark, no second tab, no back button, and no way to send
 * somebody "look at this ledger". Opening a rail item in a new tab landed on the
 * dashboard, because there was nothing in the address to land on.
 *
 * This is deliberately not a routing library. There is one shape of address —
 * `/{module}/{item}` — and both halves are validated against `MODULES`, so the
 * URL cannot select a screen the nav does not offer. A router earns its place
 * when routes take parameters and nest; when this application's screens do (an
 * account code in the ledger's address, a voucher number in a deep link), that
 * is the moment to bring one in, and this module is the seam it replaces.
 *
 * The API lives under `/api`, which nginx proxies before the SPA fallback sees
 * it, so no application address can collide with one.
 */

export interface Route {
  module: string;
  item: string;
}

/** Where an unrecognised address lands, and where the app opens. */
export const HOME: Route = { module: 'dashboard', item: 'overview' };

/** `/finance/gl`. The one address shape. */
export function pathFor(route: Route): string {
  return `/${route.module}/${route.item}`;
}

/**
 * The route an address names, or {@link HOME} if it names nothing.
 *
 * Both halves are checked: a module that exists and an item that exists **in
 * that module**, so `/finance/products` is not silently accepted as the
 * inventory screen. A module on its own — `/finance` — opens its first item,
 * which is what a typed or truncated address most likely meant.
 *
 * Permissions are *not* consulted here. What a user may see is decided by the
 * API and applied in `App`, which drops a module the session cannot reach and
 * moves off it; a URL parser that also filtered would be a second copy of that
 * rule, and the copy that gets it wrong is the one nobody is looking at.
 */
export function routeFor(pathname: string): Route {
  const [moduleKey, itemKey] = pathname.split('/').filter(Boolean);
  const owner = MODULES.find((m) => m.key === moduleKey);
  if (!owner) return HOME;

  const item = owner.items.find((i) => i.key === itemKey) ?? owner.items[0];
  return item ? { module: owner.key, item: item.key } : HOME;
}

/** Which module owns a nav key, for navigation that knows only the screen. */
export function moduleOf(itemKey: string): string | undefined {
  return MODULES.find((m) => m.items.some((i) => i.key === itemKey))?.key;
}

/**
 * The module map with an `href` on every module and every item.
 *
 * The shell renders an `<a>` wherever it finds one, which is what makes
 * ⌘-click, middle-click and "Open in new tab" work on the rail. Pure, and it
 * copies rather than mutates, so `withBadges` can be composed with it either way
 * round.
 */
export function withHrefs(modules: NavModule[]): NavModule[] {
  return modules.map((module) => ({
    ...module,
    href: module.items[0] && pathFor({ module: module.key, item: module.items[0].key }),
    items: module.items.map((item) => ({
      ...item,
      href: pathFor({ module: module.key, item: item.key }),
    })),
  }));
}

/**
 * The current route, and the way to change it.
 *
 * `pushState` rather than a hash, because the addresses should look like
 * addresses — and because both servers already cooperate: Vite falls back to
 * `index.html` for any path in development, and `docker/nginx/default.conf`
 * does the same in production, which is what makes a deep link survive a
 * refresh rather than 404.
 *
 * `popstate` is the back button; it fires for history the browser moves through
 * and never for our own `pushState`, so the state here and the address bar
 * cannot drift.
 */
export function useRoute(): {
  route: Route;
  navigate: (next: Route, options?: { replace?: boolean }) => void;
} {
  const [route, setRoute] = useState<Route>(() => routeFor(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(routeFor(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: Route, options?: { replace?: boolean }) => {
    const path = pathFor(next);
    if (path !== window.location.pathname) {
      // The query string is carried through: nothing reads one yet, and losing
      // one silently on every click is the kind of thing that is discovered
      // much later by whatever first puts something there.
      const url = path + window.location.search;
      if (options?.replace) window.history.replaceState(null, '', url);
      else window.history.pushState(null, '', url);
    }
    setRoute(next);
  }, []);

  /**
   * `/` is a real address, and it should not stay one.
   *
   * Landing on the root — or on a path that resolved to something else, like
   * `/finance` opening the chart of accounts — leaves the bar showing an address
   * that does not name what is on screen. Replacing rather than pushing means
   * the corrected address does not become a history entry the back button has to
   * step through on the way out of the application.
   */
  useEffect(() => {
    if (pathFor(route) !== window.location.pathname) {
      window.history.replaceState(null, '', pathFor(route) + window.location.search);
    }
  }, [route]);

  return useMemo(() => ({ route, navigate }), [route, navigate]);
}
